import React, { useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { PRODUCT_ADDED_TO_CART } from '../../../constants/messages';
import { showAlert } from '../../../store/CommonSlice';
import type { AppDispatch, RootState } from '../../../store/store';
import { setProductToCart, setToLocalStorage } from '../../../store/UserSlice';
import { AlertType, type CartItem } from '../../../types/common';
import QuantityBlock from '../../showcase/QuantityBlock/QuantityBlock';
import Action from '../Action/Action';
import './addToCartControl.scss';

function joinClasses(...parts: Array<string | undefined | null | false>): string {
    return parts.filter(Boolean).join(' ');
}

const stopPropagationOnly = (e: React.SyntheticEvent) => {
    e.stopPropagation();
};

const stopAll = (e: React.SyntheticEvent) => {
    e.preventDefault();
    e.stopPropagation();
};

export type AddToCartControlProps = {
    product: CartItem;
    /** Обертка компонента */
    className?: string;
    /** Куда вести по кнопке "перейти в корзину" */
    cartPath?: string; // default: "/cart"
    /** Иконка корзины в активном состоянии (split) */
    cartIconSrc?: string; // default: "/icons/cart.svg"
    cartIconAlt?: string; // default: "Корзина"
    /** Показать/скрыть правую кнопку перехода в корзину */
    showGoToCartButton?: boolean; // default: true
    /** Доп. класс для кнопки Action в состоянии "не в корзине" */
    addActionExtraClass?: string;
    /** Текст кнопки добавления */
    addButtonText?: string; // default: "Добавить в корзину"
    /** Коллбек после успешного добавления */
    onAdded?: () => void;
    /** Настройки QuantityBlock */
    quantityBackground?: string;
    disableDecrement?: boolean;
    /** Размеры активного "split" блока */
    activeWidth?: string; // default: "10.2rem"
    activeHeight?: string; // default: "2.2rem"
};

const AddToCartControl: React.FC<AddToCartControlProps> = ({
    product,
    className,
    cartPath = '/cart',
    cartIconSrc = '/icons/new/cart.svg',
    cartIconAlt = 'Корзина',
    showGoToCartButton = true,
    addActionExtraClass,
    addButtonText = 'Добавить в корзину',
    onAdded,
    quantityBackground = '#fff',
    disableDecrement = false,
    activeWidth = '30rem',
    activeHeight = '3rem',
}) => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const { cart } = useSelector((state: RootState) => state.user);
    const productInCart = useMemo(() => cart.find((it) => it.productId === product.productId), [cart, product.productId]);
    const isInCart = (productInCart?.quantity ?? 0) > 0;
    const rootClassName = joinClasses('add-to-cart-control', isInCart && 'add-to-cart-control--active', className);

    const rootStyle = useMemo(() => {
        const s: React.CSSProperties & Record<string, string> = {};
        s['--atcc-width'] = activeWidth;
        s['--atcc-height'] = activeHeight;
        return s;
    }, [activeWidth, activeHeight]);

    const addToCartHandler = useCallback(
        (e: React.MouseEvent<HTMLButtonElement>) => {
            stopAll(e);
            dispatch(setProductToCart(product));
            dispatch(setToLocalStorage('cart'));
            dispatch(showAlert({ type: AlertType.Success, message: PRODUCT_ADDED_TO_CART, action: 'cart' }));
            onAdded?.();
        },
        [dispatch, onAdded, product],
    );
    const goToCartHandler = useCallback(
        (e: React.MouseEvent<HTMLButtonElement>) => {
            stopAll(e);
            navigate(cartPath);
        },
        [navigate, cartPath],
    );

    if (!isInCart) {
        return (
            <div className={rootClassName} style={rootStyle}>
                <Action
                    as="button"
                    type="button"
                    onClick={addToCartHandler}
                    variant="outline"
                    extraClass={joinClasses('add-to-cart-control__cartBtn', addActionExtraClass)}
                >
                    {addButtonText}
                </Action>
            </div>
        );
    }

    return (
        <div className={rootClassName} style={rootStyle}>
            <div className="add-to-cart-control__split" onMouseDown={stopPropagationOnly} onClick={stopPropagationOnly}>
                <div className="add-to-cart-control__left">
                    <QuantityBlock id={product.productId} disableDecrement={disableDecrement} background={quantityBackground} />
                </div>
                {showGoToCartButton && (
                    <div className="add-to-cart-control__right" title="Перейти в корзину">
                        <button className="add-to-cart-control__goCart" type="button" onClick={goToCartHandler} aria-label="Перейти в корзину">
                            Перейти в корзину
                            <img className="add-to-cart-control__goCartImg" src={cartIconSrc} alt={cartIconAlt} />
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AddToCartControl;
